import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DLCompany } from './company';
import { NotifierService, DialogService } from '../../ui';
import { DLLedger } from './ledger';
import * as moment from 'moment-timezone';
import { first, isEmpty, last, map, pick, remove } from 'lodash';
import { DLDocument } from './document';
import { CreateDLDocumentInput, DLDocumentService, DLTemporaryDocument } from './document.service';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { FormControl } from '@angular/forms';

type UploadStatus = 'START' | 'UPLOADING' | 'PROCESSING' | 'DONE' | 'UNAUTHENTICATED' | 'UNVERIFIED' | 'ERROR';

interface Verification {
  rootCertificateName?: string
  rootCertificatePem?: string
  verified: boolean
  authenticity: boolean
  integrity: boolean
  expired: boolean
  meta: any
  unverifiedReason?: any
}

interface UploadItem {
  tmp: DLTemporaryDocument
  model: DLDocument
  verification: Verification
  status: UploadStatus
  progress: number
  timeout?: any
  date?: FormControl
  comment?: FormControl
}

interface DocumentItem {
  model: DLDocument
  edit: boolean
  date?: FormControl
  comment?: FormControl
}

function getVerificationStatus(ledger: DLLedger, data: Verification): UploadStatus {
  if (!ledger.signed) {
    return 'DONE';
  }

  if (data.verified) {
    return 'DONE';
  }
  else if (data.integrity && !data.authenticity) {
    return 'UNAUTHENTICATED';
  }
  else {
    return 'UNVERIFIED';
  }
}

function getUnverifiedReason(verification: Verification) {
  if (!verification.integrity) {
    return {
      message: `l'intégrité du document n'a pas été vérifiée`,
    };
  }
  
  if (verification.expired) {
    return {
      message: `Le certificat du document a expiré`,
    }
  }

  if (!verification.authenticity) {
    return {
      message: `La signature du document n'a pas pu être authentifiée`,
    }
  }
}

@Component({
  templateUrl: './document-edit.component.html',
})
export class DLDocumentEditComponent implements OnInit {

  company: DLCompany
  ledger: DLLedger

  title: string

  isFileOver: boolean = false
  uploader: FileUploader

  items: UploadItem[] = [];
  documents :DocumentItem[];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private documentService: DLDocumentService,
    private notifier: NotifierService,
    private dialogService: DialogService
  ) { }

  initForm() {
    const ledger = this.ledger;

    this.uploader = new FileUploader({
      autoUpload: false,
    });

    this.uploader.onBuildItemForm = (fileItem: FileItem, form: FormData) => {
      const item = fileItem.formData as UploadItem;

      // Build form data
      for (const { key, value } of item.tmp.uploadForm) {
        form.append(key, value);
      }

      // Set the url
      fileItem.url = item.tmp.uploadURL;
      fileItem.withCredentials = false;

      return fileItem;
    }

    this.uploader.onProgressItem = (fileItem: FileItem, progress: number) => {
      const item = fileItem.formData as UploadItem;
      
      item.progress = progress;
    }
    
    this.uploader.onCompleteItem = (fileItem: FileItem, response: any, status: number) => {
      const upload = fileItem.formData as UploadItem;

      if (status < 200 || status >= 400) {
        upload.status = 'ERROR';
        return;
      }

      // Poll for processing
      if (this.ledger.signed) {
        upload.status = 'PROCESSING';
      }
      const timeout = 1000;
      const documentService = this.documentService;

      function getProcessing() {
        documentService
        .processedTemporaryDocument(upload.model.id)
        .subscribe((result) => {
          if (result) {
            upload.verification = pick(result, 'rootCertificateName', 'rootCertificatePem', 'verified', 'authenticity', 'integrity', 'expired', 'meta');

            upload.model.filetype = result.filetype;
            upload.model.filesize = result.filesize;
            upload.model.createdAt = result.createdAt;

            upload.model.date = moment(new Date()).format('YYYY-MM-DD');

            upload.timeout = null;
            
            upload.status = getVerificationStatus(ledger, upload.verification);

            if (upload.status !== 'DONE') {
              upload.verification.unverifiedReason = getUnverifiedReason(upload.verification);
            }
          }
          else {
            upload.timeout = setTimeout(getProcessing, timeout);
          }
        }, (error) => {
          upload.status = 'ERROR';
        });
      }

      upload.timeout = setTimeout(getProcessing, timeout);
    }
  }

  ngOnInit() {

    this.title = this.route.routeConfig.data.title;

    // Get the company + ledger
    this.notifier.startProgress();

    this.route.data.subscribe((data) => {
      this.company = data.company;
      this.ledger = first(this.company.ledgers);

      this.initForm();

      this.documents = map(this.ledger.documents, model => ({
        model,
        edit: false,
      }));

      this.notifier.endProgress();
    });
  }

  cancel() {
    this.router.navigate(['documents-ledger', 'companies', this.company.id, this.ledger.id]);
  }

  addDocument(item: UploadItem) {
    this.notifier.startProgress();

    let input: CreateDLDocumentInput = {
      processedDocumentId: item.model.id,
      date: item.model.date,
      comment: item.model.comment,
    }

    if (this.ledger.signed) {
      if (item.status === 'UNAUTHENTICATED') {
        const cert = last<any>(item.verification.meta.certs);
        
        if (cert) {
          input.rootCertificatePem = cert.pemCertificate;
        }
      }
    }

    this.documentService
      .createDocument(this.company.id, this.ledger.id, input)
      .subscribe((document) => {
        this.notifier.success('Document ajouté avec succès');

        this.removeItem(item);

        this.ledger.documents.splice(0, 0, document);
        this.documents.splice(0, 0, {
          model: document,
          edit: false,
        });

        this.notifier.endProgress();

      }, (err) => {
        this.notifier.endProgress();
      });
    
    return false;
  }

  edit(item: DocumentItem) {
    item.edit = true;
    
    return false;
  }

  cancelEdit(item: DocumentItem) {
    item.edit = false;
    item.date = null;
    item.comment = null;
  }

  update(item: DocumentItem) {
    this.notifier.startProgress();

    this.documentService
      .updateDocument(this.company.id, this.ledger.id, item.model.id, {
        date: moment(item.model.date).format('YYYY-MM-DD'),
        comment: item.model.comment,
      })
      .subscribe((document) => {
        this.notifier.success('Document modifié avec succès');

        item.model = document;
        item.edit = false;

        this.notifier.endProgress();

      }, (err) => {
        this.notifier.endProgress();
      });
    
    return false;
  }

  delete(document: DLDocument) {
    this.dialogService
      .confirmDelete('Voulez-vous supprimer ce document?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.documentService
          .delete(this.company.id, this.ledger.id, document.id)
          .subscribe((id) => {
            this.notifier.success('Document supprimé avec succès');

            remove(this.ledger.documents, d => d.id === id);
            remove(this.documents, d => d.model === document);

            this.notifier.endProgress();

          }, (err) => {
            this.notifier.endProgress();
          });
      });
    
    return false;
  }

  onUploadItemAdded() {
    for (let fi of this.uploader.queue) {
      if (isEmpty(fi.formData)) {

        fi.formData = 'processing';

        this.addUploadItem(fi);
      }
    }
  }

  addUploadItem(fi: FileItem) {

    const item: UploadItem = {
      tmp: null,
      model: new DLDocument({
        filename: fi.file.name,
        filetype: fi.file.type,
        filesize: fi.file.size,
      }),
      verification: null,
      status: 'START',
      progress: 0,
    }

    this.items.push(item);
    fi.formData = item;

    this.documentService.createTemporaryDocument(this.company.id, this.ledger.id, {
      filename: fi.file.name,
      filetype: fi.file.type,
    })
    .subscribe((td) => {
      item.model.id = td.id;
      item.status = 'UPLOADING';
      item.tmp = td;

      this.uploader.uploadItem(fi);
    });
  }

  removeItem(item: UploadItem) {
    if (item.timeout) {
      clearTimeout(item.timeout);
    }

    remove(this.items, i => i === item);
  }
}
